import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

// Import product images
import ppRope1 from "../../assets/products/ropes/pp-rope-1.jpg";
import ppRope2 from "../../assets/products/ropes/pp-rope-2.jpg";
import uClamp1 from "../../assets/products/clamps/u-clamp-1.jpg";
import uClamp2 from "../../assets/products/clamps/u-clamp-2.jpg";
import dShackle1 from "../../assets/products/shackles/d-shackle-1.jpg";
import dShackle2 from "../../assets/products/shackles/d-shackle-2.jpg";
import chainPulley1 from "../../assets/products/pulley/chain-pulley-1.jpg";
import chainPulley2 from "../../assets/products/pulley/chain-pulley-2.jpg";
import wireRope1 from "../../assets/products/ropes/wire-rope-1.jpg";
import wireRope2 from "../../assets/products/ropes/wire-rope-2.jpg";
import webSling1 from "../../assets/products/slings/web-sling-1.jpg";
import webSling2 from "../../assets/products/slings/web-sling-2.jpg";

const RopesClambs = () => {
  const products = [
    {
      title: "Polypropylene Rope",
      description: "A PP rope (Polypropylene rope) is a strong, lightweight, and versatile rope widely used in industries such as construction, marine, agriculture, transportation, and safety. It is known for its high strength, water resistance, and durability.",
      color: "text-gray-900",
      icon: "",
      images: [ppRope1, ppRope2],
      features: [
        "High tensile strength",
        "Water resistant",
        "Lightweight construction",
        "UV stabilized",
        "Chemical resistant"
      ],
      applications: [
        "Construction",
        "Marine operations",
        "Agriculture",
        "Transportation",
        "Safety equipment"
      ]
    },
    {
      title: "U Clamp",
      description: "A U clamp, also known as a U-bolt clamp, is a fastening device used to secure pipes, tubes, rods, and cables in various industrial, construction, and automotive applications. It is shaped like the letter 'U' with threaded ends for easy tightening.",
      color: "text-gray-900",
      icon: "🔧",
      images: [uClamp1, uClamp2],
      features: [
        "Durable steel construction",
        "Threaded ends for secure fastening",
        "Various sizes available",
        "Corrosion-resistant options",
        "Easy installation"
      ],
      applications: [
        "Pipe securing",
        "Cable management",
        "Industrial fastening",
        "Automotive use",
        "Construction projects"
      ]
    },
    {
      title: "D Shackle",
      description: "A D shackle, also known as a Dee shackle, is a U-shaped metal connector with a removable pin or bolt used for securing, lifting, or rigging loads. It is widely used in industries such as construction, marine, lifting, rigging, and fabrication.",
      color: "text-gray-900",
      icon: "⚓",
      images: [dShackle1, dShackle2],
      features: [
        "High load capacity",
        "Removable pin design",
        "Forged steel construction",
        "Safety certification",
        "Multiple size options"
      ],
      applications: [
        "Heavy lifting",
        "Marine rigging",
        "Construction work",
        "Industrial fabrication",
        "Secure load attachment"
      ]
    },
    {
      title: "Chain Pulley Block",
      description: "A Chain Pulley Block, also known as a manual chain hoist, is a mechanical device used for lifting and lowering heavy loads with minimal effort. It is commonly used in factories, warehouses, construction sites, fabrication workshops, and industrial plants.",
      color: "text-gray-900",
      icon: "⛓️",
      images: [chainPulley1, chainPulley2],
      features: [
        "High lifting capacity",
        "Manual operation",
        "Safety brake system",
        "Durable chain construction",
        "Easy maintenance"
      ],
      applications: [
        "Factory operations",
        "Warehouse logistics",
        "Construction sites",
        "Workshop lifting",
        "Industrial maintenance"
      ]
    },
    {
      title: "Wire Rope Sling",
      description: "A Wire Rope Sling is a strong, flexible lifting accessory used for hoisting, rigging, and material handling in industries such as construction, manufacturing, shipbuilding, and heavy engineering. It is made of steel wire strands twisted together to form a durable lifting sling.",
      color: "text-gray-900",
      icon: "🪢",
      images: [wireRope1, wireRope2],
      features: [
        "High tensile strength",
        "Flexible construction",
        "Abrasion resistant",
        "Various end fittings",
        "Load tested certification"
      ],
      applications: [
        "Heavy lifting operations",
        "Construction rigging",
        "Shipbuilding",
        "Manufacturing",
        "Material handling"
      ]
    },
    {
      title: "Web Sling",
      description: "A Web Sling, also known as a Polyester Lifting Sling or Flat Webbing Sling, is a flexible and lightweight lifting accessory used for hoisting, rigging, and material handling in industries such as construction, manufacturing, logistics, and shipbuilding. It is made from high-strength synthetic fibers, typically polyester, nylon, or polypropylene.",
      color: "text-gray-900",
      icon: "🎗️",
      images: [webSling1, webSling2],
      features: [
        "Lightweight design",
        "High strength-to-weight ratio",
        "Non-marring surface",
        "Color-coded capacity",
        "Multiple configurations"
      ],
      applications: [
        "Construction lifting",
        "Manufacturing operations",
        "Logistics handling",
        "Shipyard operations",
        "Delicate load lifting"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-[160px]"> {/* Added padding for fixed header */}
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-b from-gray-900 to-gray-800">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:60px_60px]" />
        <div className="absolute inset-0 bg-gradient-to-br from-gray-800/50 via-transparent to-gray-800/50" />

        <div className="container mx-auto px-4 relative">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-5xl md:text-6xl font-bold text-white mb-6"
            >
              Ropes, Clamps & Slings
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-white max-w-3xl mx-auto"
            >
              High-quality lifting and securing solutions for industrial applications
            </motion.p>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              transition={{ duration: 1.5, delay: 0.5 }}
              className="mt-16 h-1 max-w-xl mx-auto bg-gradient-to-r from-transparent via-gray-400 to-transparent"
            />
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            {products.map((product, idx) => (
              <motion.div
                key={product.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-300"
              >
                {/* Image Gallery */}
                <div className="relative h-64 overflow-hidden">
                  <div className="absolute inset-0 flex transition-transform duration-500 hover:scale-105">
                    {product.images.map((image, imageIdx) => (
                      <img
                        key={imageIdx}
                        src={image}
                        alt={`${product.title} ${imageIdx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    ))}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex items-center gap-4">
                      <div>
                        {/* <h2 className={`text-3xl font-bold text-white mb-2`}>
                          {product.title}
                        </h2> */}
                        {/* <div className="w-32 h-1 bg-gray-900"></div> */}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-8">
                  <h2 className={`text-3xl font-bold text-black mb-2`}>
                    {product.title}
                  </h2>
                  <div className="w-32 h-1 bg-gray-200 mb-2"></div>
                  <p className="text-black text-lg mb-6">
                    {product.description}
                  </p>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">
                        Key Features
                      </h3>
                      <ul className="list-disc list-inside space-y-2 text-black">
                        {product.features.map((feature, featureIdx) => (
                          <li key={featureIdx}>{feature}</li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">
                        Applications
                      </h3>
                      <ul className="list-disc list-inside space-y-2 text-black">
                        {product.applications.map((application, appIdx) => (
                          <li key={appIdx}>{application}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety Notice Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
            <h2 className="text-3xl font-bold text-black mb-6">
              Safety Guidelines
            </h2>
            <div className="space-y-4 text-black">
              <p>
                Proper use and maintenance of lifting equipment is crucial for workplace safety.
                Always follow manufacturer guidelines and safety protocols when using ropes, clamps, and slings.
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>Inspect equipment before each use</li>
                <li>Never exceed rated load capacity</li>
                <li>Use appropriate equipment for the task</li>
                <li>Maintain proper storage conditions</li>
                <li>Replace damaged or worn equipment immediately</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Need Expert Advice?
            </h2>
            <p className="text-gray-900 mb-8">
              Our specialists are here to help you choose the right equipment for your specific requirements
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 text-lg font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-full transition-all duration-300 transform hover:scale-105"
            >
              Contact Our Experts
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RopesClambs;